import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { compressImage } from "@/lib/imageProcessing";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import {
  ArrowLeft, MagnifyingGlass, UploadSimple, Sparkle, Warning,
  FloppyDisk, CheckCircle, X, Images, Plus, DownloadSimple, Lightning, Crown, Check, ChartLineUp, LinkSimple,
  Camera, ListChecks, Trash, Circle, SealCheck, MagicWand, Gear,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PricingPanel from "@/components/PricingPanel";
import UnderTheHoodSheet from "@/components/UnderTheHoodSheet";
import { useAuth } from "@/context/AuthContext";

const SLOTS = 12;

const PHOTO_SHOTS = [
  "Full piece, front — this is your cover shot",
  "The back of the piece",
  "Left and right sides",
  "Top, and the underside where relevant",
  "Close-ups of unique features, hardware & materials",
  "Honest close-ups of any flaws, wear or repairs",
  "Maker's marks, labels, stamps or signatures",
  "Open drawers, doors or interior (if applicable)",
  "Any included accessories or extra pieces",
];
const PHOTO_REQS = [
  "High resolution — at least 1200px on the shortest side",
  "JPG, PNG or HEIC · under 10MB each",
  "Minimum 3 photos · up to 12 per item",
  "No watermarks, text overlays or signatures on the image",
];
const PHOTO_TIPS = [
  "Natural light is best — avoid harsh shadows",
  "Center the piece in the frame",
  "Keep a consistent style across all your listings",
  "Adjust brightness, contrast & sharpness; crop out distractions",
  "Represent the item accurately — what they see is what they get",
];

// Vintage-native controlled vocabularies
const ATTR_BASIS = ["Unmarked", "In the style of", "Attributed to", "Signed", "Labeled", "Stamped / branded", "Documented"];
const COND_TYPE = ["Antique (100+ yrs)", "Vintage", "Used", "New old stock"];
const ORIG_COND = ["Unaltered / original", "Original design modified", "Restored / refinished", "Reupholstered", "Unknown"];
const COND_LEVEL = ["Excellent", "Good — some imperfections", "Fair — needs restoration"];
const UNIT_TYPE = ["Single piece", "Pair", "Set", "Suite"];

const numField = (v) => (v === null || v === undefined || v === "" ? "" : v);

export default function ReviewWorkspace() {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [demoFilling, setDemoFilling] = useState(false);
  const [engineOpen, setEngineOpen] = useState(false);
  const [listings, setListings] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkPhotos, setLinkPhotos] = useState(true);
  const [linkBusy, setLinkBusy] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [usage, setUsage] = useState(null);
  const [suggestions, setSuggestions] = useState({});
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [readinessOpen, setReadinessOpen] = useState(false);
  const [readiness, setReadiness] = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [readinessPlatform, setReadinessPlatform] = useState("chairish");
  const fileRef = useRef();

  const currentId = selected?.id;

  const loadQueue = useCallback(async () => {
    const [{ data: ls }, { data: um }, { data: us }, { data: sg }] = await Promise.all([
      api.get("/listings"),
      api.get("/images/unmatched"),
      api.get("/usage"),
      api.get("/suggestions"),
    ]);
    setListings(ls);
    setUnmatched(um);
    setUsage(us);
    setSuggestions(sg || {});
    return ls;
  }, []);

  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    const { data } = await api.get(`/listings/${id}`);
    setSelected(data);
    setForm({
      listing_headline: data.listing_headline || "",
      brand_designer: data.brand_designer || "",
      period_era: data.period_era || "",
      material: data.material || "",
      width_inches: numField(data.width_inches),
      depth_inches: numField(data.depth_inches),
      height_inches: numField(data.height_inches),
      weight_lbs: numField(data.weight_lbs),
      price_retail: numField(data.price_retail),
      price_minimum_acceptable: numField(data.price_minimum_acceptable),
      ai_guide_notes: data.ai_guide_notes || "",
      editable_final_description: data.editable_final_description || "",
      styles: data.styles || "",
      condition: data.condition || "",
      country_of_origin: data.country_of_origin || "",
      maker: data.maker || "",
      artist: data.artist || "",
      styled_after: data.styled_after || "",
      attribution_basis: data.attribution_basis || "",
      item_condition_type: data.item_condition_type || "",
      original_condition: data.original_condition || "",
      condition_level: data.condition_level || "",
      dominant_color: data.dominant_color || data.color || "",
      accent_colors: data.accent_colors || "",
      unit_type: data.unit_type || "",
      pieces_included: data.pieces_included || "",
      inventory_count: data.inventory_count ?? "",
      seat_height_inches: numField(data.seat_height_inches),
      arm_height_inches: numField(data.arm_height_inches),
      seat_interior_w_inches: numField(data.seat_interior_w_inches),
      seat_interior_d_inches: numField(data.seat_interior_d_inches),
      num_seats: data.num_seats ?? "",
      flaws: Array.isArray(data.flaws) ? data.flaws : [],
    });
  }, []);

  useEffect(() => {
    (async () => {
      const ls = await loadQueue();
      const target = listingId || ls[0]?.id;
      if (target) loadDetail(target);
    })();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (listingId && listingId !== currentId) loadDetail(listingId);
  }, [listingId]); // eslint-disable-line

  const [searchParams] = useSearchParams();
  const focusField = searchParams.get("focus");
  useEffect(() => {
    if (!selected || !focusField) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="${focusField}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        try { el.focus({ preventScroll: true }); } catch (e) { /* noop */ }
        el.classList.add("ring-2", "ring-[#FBBF24]", "rounded-md");
        setTimeout(() => el.classList.remove("ring-2", "ring-[#FBBF24]"), 2600);
      }
    }, 550);
    return () => clearTimeout(t);
  }, [selected?.id, focusField]); // eslint-disable-line

  // Coming straight from a Fantastic Finds scan → auto-open the readiness check once, so the seller
  // immediately sees how close the AI got them and what's left. (?fromfind=1 on the promote nav.)
  const fromFind = searchParams.get("fromfind");
  const readinessAutoOpened = useRef(false);
  useEffect(() => {
    if (!selected || !fromFind || readinessAutoOpened.current) return;
    readinessAutoOpened.current = true;
    const t = setTimeout(() => { openReadiness(); }, 950);
    return () => clearTimeout(t);
  }, [selected?.id, fromFind]); // eslint-disable-line

  const selectListing = (id) => {
    navigate(`/review/${id}`);
    loadDetail(id);
  };

  const onField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addFlaw = () => setForm((f) => ({ ...f, flaws: [...(f.flaws || []), { note: "", image_id: "" }] }));
  const updateFlaw = (i, key, val) => setForm((f) => {
    const flaws = [...(f.flaws || [])];
    flaws[i] = { ...flaws[i], [key]: val };
    return { ...f, flaws };
  });
  const removeFlaw = (i) => setForm((f) => ({ ...f, flaws: (f.flaws || []).filter((_, idx) => idx !== i) }));

  const pickSuggestion = (key, val, isToken) => {
    if (isToken) {
      const cur = (form[key] || "").trim();
      const parts = cur ? cur.split(/[,/]/).map((s) => s.trim()).filter(Boolean) : [];
      if (parts.some((p) => p.toLowerCase() === val.toLowerCase())) return;
      onField(key, parts.length ? `${parts.join(", ")}, ${val}` : val);
    } else {
      onField(key, val);
    }
  };

  const demoFill = async () => {
    if (!currentId) return;
    setDemoFilling(true);
    try {
      await api.put(`/listings/${currentId}`, buildPayload());
      await api.post(`/listings/${currentId}/demo-fill`);
      await loadDetail(currentId);
      await loadQueue();
      toast.success("Filled the remaining details with sample data — ready to demo");
    } catch (e) {
      toast.error("Couldn't run the demo fill");
    } finally {
      setDemoFilling(false);
    }
  };

  const buildPayload = () => {
    const p = {};
    const floats = ["width_inches", "depth_inches", "height_inches", "weight_lbs", "price_retail", "price_minimum_acceptable", "seat_height_inches", "arm_height_inches", "seat_interior_w_inches", "seat_interior_d_inches"];
    const ints = ["inventory_count", "num_seats"];
    Object.entries(form).forEach(([k, v]) => {
      if (floats.includes(k)) p[k] = v === "" || v == null ? null : parseFloat(v);
      else if (ints.includes(k)) p[k] = v === "" || v == null ? null : parseInt(v, 10);
      else p[k] = v;
    });
    return p;
  };

  const saveDraft = async (silent = false) => {
    if (!currentId) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/listings/${currentId}`, buildPayload());
      setSelected((s) => ({ ...s, ...data }));
      await loadQueue();
      if (!silent) toast.success("Draft saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    if (!currentId) return;
    if (selected?.status !== "Exported" && usage?.locked) {
      setShowUpgrade(true);
      return;
    }
    try {
      await api.put(`/listings/${currentId}`, buildPayload());
      await api.post(`/listings/${currentId}/approve`);
      toast.success("Approved & queued for cross-listing");
      await loadQueue();
      loadDetail(currentId);
    } catch (e) {
      if (e.response?.status === 402) {
        setShowUpgrade(true);
      } else {
        toast.error("Couldn't approve this listing");
      }
    }
  };

  const openReadiness = async () => {
    if (!currentId) return;
    setReadiness(null);
    setReadinessLoading(true);
    setReadinessOpen(true);
    try {
      await api.put(`/listings/${currentId}`, buildPayload());
      const { data } = await api.get(`/listings/${currentId}/readiness`);
      setReadiness(data);
      if (data.platforms?.length && !data.platforms.find((p) => p.key === readinessPlatform)) {
        setReadinessPlatform(data.platforms[0].key);
      }
    } catch (e) {
      toast.error("Couldn't run the readiness check");
      setReadinessOpen(false);
    } finally {
      setReadinessLoading(false);
    }
  };

  const handleCrossList = () => {
    if (usage?.locked && selected?.status !== "Exported") {
      setShowUpgrade(true);
      return;
    }
    openReadiness();
  };

  const proceedCrossList = async () => {
    setReadinessOpen(false);
    await approve();
  };

  const enrichCurrent = async () => {
    if (!currentId) return;
    setEnriching(true);
    try {
      const { data } = await api.post(`/listings/${currentId}/enrich`);
      toast.success(`Pulled details + ${data.images_imported} photos from Chairish`);
      await loadDetail(currentId);
      await loadQueue();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enrichment failed");
    } finally {
      setEnriching(false);
    }
  };

  const enrichFromLink = async () => {
    const url = linkUrl.trim();
    if (!url || !currentId) return;
    const guarded = ["brand_designer", "period_era", "material", "styles", "condition",
      "country_of_origin", "width_inches", "depth_inches", "height_inches"];
    const filled = guarded.filter((k) => String(form[k] ?? "").trim() !== "");
    if (filled.length) {
      const ok = window.confirm(
        "Auto-Fill will replace the details you've already entered (maker, period, materials, dimensions, condition, etc.) with what it reads from this link.\n\nYour price is never changed. Continue and overwrite these fields?"
      );
      if (!ok) return;
    }
    setLinkBusy(true);
    try {
      const { data } = await api.post(`/listings/${currentId}/enrich-from-url`, { url, pull_images: linkPhotos });
      const label = data.source_platform === "1stdibs" ? "1stDibs" : data.source_platform === "chairish" ? "Chairish" : "the link";
      toast.success(`Pulled the details${data.images_imported ? ` + ${data.images_imported} photos` : ""} from ${label} — just add your price.`);
      setLinkOpen(false);
      setLinkUrl("");
      await loadDetail(currentId);
      await loadQueue();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't read that link — check it and try again.");
    } finally {
      setLinkBusy(false);
    }
  };

  const extractFields = async () => {
    if (!currentId) return;
    const hasText = (form.editable_final_description || selected?.original_description || "").trim().length >= 30;
    if (!hasText) { toast.message("Add an accurate description first — then I'll fill in the details."); return; }
    setExtracting(true);
    try {
      await saveDraft(true);
      const { data } = await api.post(`/listings/${currentId}/extract-fields`);
      const n = (data.fields_filled || []).length;
      toast.success(n
        ? `Filled in ${n} detail${n > 1 ? "s" : ""} from your description — review & add your price.`
        : "Your fields already look complete — nothing new to fill.");
      await loadDetail(currentId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't read that description — try again.");
    } finally {
      setExtracting(false);
    }
  };

  const generateAI = async () => {
    if (!currentId) return;
    setGenerating(true);
    try {
      await saveDraft(true);
      const { data } = await api.post(`/listings/${currentId}/generate-description`);
      onField("editable_final_description", data.description);
      setSelected((s) => ({ ...s, is_ai_generated: true }));
      toast.success("AI description generated");
      try { const { data: us } = await api.get("/usage"); setUsage(us); } catch (e) { /* noop */ }
    } catch (e) {
      if (e.response?.status === 402) {
        try { const { data: us } = await api.get("/usage"); setUsage(us); } catch (err) { /* noop */ }
        setShowUpgrade(true);
      } else {
        toast.error(e.response?.data?.detail || "AI generation failed");
      }
    } finally {
      setGenerating(false);
    }
  };

  const uploadFiles = async (files) => {
    if (!currentId || !files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (!file.type.startsWith("image/")) continue;
        const compressed = await compressImage(file);
        const fd = new FormData();
        fd.append("file", compressed);
        fd.append("listing_id", currentId);
        await api.post("/images/upload", fd);
      }
      await loadDetail(currentId);
      await loadQueue();
      toast.success("Photos added");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    const imgId = e.dataTransfer.getData("unmatched-image-id");
    if (imgId) {
      await api.put(`/images/${imgId}`, { listing_id: currentId });
      await loadDetail(currentId);
      await loadQueue();
      toast.success("Photo matched");
      return;
    }
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) uploadFiles(files);
  };

  const togglePadding = async (img) => {
    await api.put(`/images/${img.id}`, { apply_padding: !img.apply_padding });
    loadDetail(currentId);
  };

  const removeImage = async (img) => {
    if (!window.confirm("Remove this photo from the listing?")) return;
    await api.delete(`/images/${img.id}`);
    await loadDetail(currentId);
    await loadQueue();
    toast.success("Photo removed");
  };

  const assignUnmatched = async (img) => {
    if (!currentId) return;
    await api.put(`/images/${img.id}`, { listing_id: currentId });
    await loadDetail(currentId);
    await loadQueue();
  };

  const filtered = listings.filter((l) =>
    (l.listing_headline || "").toLowerCase().includes(search.toLowerCase())
  );

  const images = selected?.images || [];
  const slots = Array.from({ length: SLOTS }, (_, i) => images.find((im) => im.slot === i) || images[i] || null);

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[#FDFBF7]">
      {/* Header */}
      <header className="border-b border-[#E5E5E5] bg-white px-5 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <button data-testid="back-to-dashboard" onClick={() => navigate("/")} className="h-9 w-9 rounded-md border border-[#E5E5E5] flex items-center justify-center hover:bg-[#FDFBF7]">
            <ArrowLeft size={18} />
          </button>
          <img src="/vv_logo.jpg" alt="Vintage Vantage seal" className="h-10 w-10 rounded-full object-cover ring-1 ring-[#E5E0D6]" data-testid="workspace-logo" />
          <span className="font-serif-display text-xl font-bold tracking-tight">Vintage Vantage</span>
          <span className="label-caps ml-2 hidden md:inline">Review Workspace</span>
        </div>
        {selected && (
          <div className="flex items-center gap-3">
            <StatusBadge status={selected.review_status} testid="header-status-badge" />
            <span className="label-caps">{selected.status}</span>
          </div>
        )}
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* LEFT — Queue */}
        <aside className="w-[24%] min-w-[240px] border-r border-[#E5E5E5] bg-[#FDFBF7] flex flex-col h-full">
          <div className="p-3 border-b border-[#E5E5E5] shrink-0">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
              <input
                data-testid="queue-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search listings…"
                className="w-full h-9 pl-9 pr-3 rounded-md border border-[#E5E5E5] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B2A]"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="listing-queue">
            {filtered.map((l) => (
              <button
                key={l.id}
                data-testid={`queue-item-${l.id}`}
                onClick={() => selectListing(l.id)}
                className={`w-full text-left p-3 border-b border-[#E5E5E5] hover:bg-white transition-colors flex flex-col gap-1.5 ${currentId === l.id ? "bg-white border-l-2 border-l-[#0D1B2A]" : ""}`}
              >
                <span className="text-sm font-medium leading-snug line-clamp-2">{l.listing_headline || "Untitled"}</span>
                <span className="text-sm text-[#525252]">{l.brand_designer || l.period_era || `SKU ${l.seller_sku || "—"}`}</span>
                <StatusBadge status={l.review_status} testid={`queue-status-${l.id}`} />
              </button>
            ))}
          </div>

          {/* Unmatched bucket */}
          <div className="border-t border-[#E5E5E5] bg-white p-3 shrink-0 max-h-[34%] overflow-y-auto" data-testid="unmatched-bucket">
            <p className="label-caps mb-2 flex items-center gap-1.5">
              <Images size={14} /> Unmatched Photos ({unmatched.length})
            </p>
            {unmatched.length === 0 ? (
              <p className="text-sm text-[#737373]">All photos are matched.</p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {unmatched.map((img) => (
                  <div
                    key={img.id}
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData("unmatched-image-id", img.id)}
                    data-testid={`unmatched-${img.id}`}
                    title={`${img.original_filename} — click to assign to selected`}
                    onClick={() => assignUnmatched(img)}
                    className="relative aspect-square rounded border border-[#E5E5E5] overflow-hidden cursor-grab hover:ring-2 hover:ring-[#0D1B2A] bg-[#FDFBF7]"
                  >
                    <img src={fileUrl(img.storage_path)} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>

        {/* CENTER — Media */}
        <section className="w-[46%] border-r border-[#E5E5E5] bg-white flex flex-col h-full overflow-y-auto p-6">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-[#A3A3A3]">Select a listing</div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-1">
                <p className="label-caps">Step 2 · Media</p>
                <button onClick={() => setChecklistOpen(true)} data-testid="photo-checklist-open" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#B47A3C] hover:text-[#0D1B2A] transition-colors">
                  <ListChecks size={16} weight="bold" /> Photo checklist
                </button>
              </div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif-display text-2xl font-semibold">Photo Studio</h2>
                <span data-testid="photo-count" className={`text-sm font-semibold ${images.length < 3 ? "text-[#B45309]" : "text-[#047857]"}`}>
                  {images.length}/{SLOTS} photos{images.length < 3 ? " · min 3" : ""}
                </span>
              </div>

              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                data-testid="media-dropzone"
                className={`border-2 border-dashed rounded-lg p-4 mb-5 transition-colors ${dragOver ? "drop-active" : "border-[#D4D4D4]"}`}
              >
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadFiles(Array.from(e.target.files || []))} data-testid="media-file-input" />
                <button onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full flex flex-col items-center gap-1.5 py-4 text-[#525252]">
                  <UploadSimple size={24} weight="light" />
                  <span className="text-base font-medium">{uploading ? "Uploading…" : "Drop photos or click to upload"}</span>
                  <span className="text-sm text-[#737373]">Auto-compressed · drag unmatched photos here to match</span>
                </button>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                {slots.map((img, i) => (
                  <div
                    key={i}
                    data-testid={`media-slot-${i}`}
                    className="relative aspect-square border border-dashed border-[#E5E5E5] rounded-md flex flex-col items-center justify-center bg-[#FDFBF7] overflow-hidden group"
                  >
                    {img ? (
                      <>
                        <img
                          src={fileUrl(img.storage_path)}
                          alt={img.original_filename}
                          className={`w-full h-full ${img.apply_padding ? "object-contain bg-white p-1" : "object-cover"}`}
                        />
                        {i === 0 && <span className="absolute top-1 right-1 bg-[#0D1B2A] text-white text-[10px] px-1.5 py-0.5 rounded">COVER</span>}
                        <label className="absolute bottom-1 left-1 right-1 flex items-center gap-1 bg-white/90 backdrop-blur rounded px-1.5 py-1 text-xs font-medium cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!img.apply_padding}
                            onChange={() => togglePadding(img)}
                            data-testid={`padding-toggle-${i}`}
                            className="h-3 w-3 accent-[#0D1B2A]"
                          />
                          White BG
                        </label>
                        <button onClick={() => removeImage(img)} data-testid={`remove-image-${i}`} className="absolute top-1 left-1 h-5 w-5 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#FEF2F2]">
                          <X size={12} className="text-[#B91C1C]" />
                        </button>
                      </>
                    ) : (
                      <button onClick={() => fileRef.current?.click()} className="flex flex-col items-center gap-1 text-[#C4C4C4] hover:text-[#0D1B2A] transition-colors">
                        <Plus size={20} />
                        <span className="text-xs">Slot {i + 1}</span>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </section>

        {/* RIGHT — Editor */}
        <section className="w-[30%] min-w-[300px] bg-[#FDFBF7] flex flex-col h-full relative">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-[#A3A3A3]">—</div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-5 space-y-5 pb-28" data-testid="content-editor">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="label-caps">Step 3 · Content</p>
                    <h2 className="font-serif-display text-2xl font-semibold">Listing Details</h2>
                    <button
                      onClick={() => setEngineOpen(true)}
                      data-testid="under-the-hood-toggle"
                      title="See the AI pipeline, readiness scoring & schema mapping behind this listing"
                      className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold text-[#6B7280] hover:text-[#B47A3C] transition-colors"
                    >
                      <Gear size={13} weight="fill" /> Under the Hood
                    </button>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0 mt-1">
                    {selected.external_product_id && (
                      <button
                        onClick={enrichCurrent}
                        disabled={enriching}
                        data-testid="enrich-current-button"
                        title="Pull dimensions, details & photos from this item's Chairish page"
                        className={`text-sm font-semibold inline-flex items-center gap-1.5 border rounded-md px-2.5 py-2 transition-colors disabled:opacity-50 ${selected.enriched ? "border-[#A7F3D0] text-[#047857] bg-[#ECFDF5]" : "border-[#0D1B2A] text-[#0D1B2A] hover:bg-[#0D1B2A] hover:text-white"}`}
                      >
                        <DownloadSimple size={13} weight={selected.enriched ? "fill" : "regular"} />
                        {enriching ? "Auto-Filling…" : selected.enriched ? "Refresh from Chairish" : "Auto-Fill from Chairish"}
                      </button>
                    )}
                    <button
                      onClick={() => setLinkOpen((o) => !o)}
                      data-testid="autofill-link-toggle"
                      title="Paste a Chairish, 1stDibs, or ANY listing/reference link to auto-fill the details (and optionally photos)"
                      className="text-sm font-semibold inline-flex items-center gap-1.5 border border-[#0D1B2A] text-[#0D1B2A] rounded-md px-2.5 py-2 hover:bg-[#0D1B2A] hover:text-white transition-colors"
                    >
                      <LinkSimple size={13} weight="bold" /> Auto-Fill from a link
                    </button>
                    <button
                      onClick={extractFields}
                      disabled={extracting}
                      data-testid="extract-fields-button"
                      title="Let AI read your description and fill in the maker, era, materials, dimensions & more"
                      className="text-sm font-semibold inline-flex items-center gap-1.5 border border-[#B47A3C] text-[#B47A3C] rounded-md px-2.5 py-2 hover:bg-[#B47A3C] hover:text-white transition-colors disabled:opacity-50"
                    >
                      <Sparkle size={13} weight="fill" /> {extracting ? "Reading…" : "Fill from description"}
                    </button>
                  </div>
                </div>

                {selected.ai_prefilled_fields?.length ? (
                  <div className="rounded-lg border border-[#A7F3D0] bg-[#ECFDF5] p-3 flex items-start gap-2.5" data-testid="ai-prefilled-banner">
                    <Sparkle size={18} weight="fill" className="text-[#047857] mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-[#065F46] leading-snug">
                        AI pre-filled {selected.ai_prefilled_fields.length} {selected.ai_prefilled_fields.length === 1 ? "detail" : "details"} from your scan — review &amp; confirm below.
                      </p>
                      <p className="text-xs text-[#047857] mt-0.5">Nothing lists without you. Add your photos, measurements &amp; price to finish.</p>
                    </div>
                    <button onClick={handleCrossList} data-testid="ai-prefilled-readiness-button"
                      className="shrink-0 text-sm font-semibold text-[#065F46] underline underline-offset-2 hover:text-[#047857]">
                      What's left?
                    </button>
                  </div>
                ) : null}

                {linkOpen && (
                  <div className="border border-[#0D1B2A] rounded-md p-3 bg-white space-y-2.5" data-testid="autofill-link-panel">
                    <p className="text-xs text-[#525252] leading-snug">
                      Paste a listing link from <strong>Chairish</strong>, <strong>1stDibs</strong>, or <strong>any website</strong> —
                      even a private seller's page or a sold-listing archive. We'll read the maker, materials, period,
                      dimensions, condition, description &amp; provenance — <strong>you just add your price</strong>.
                    </p>
                    <input
                      value={linkUrl}
                      onChange={(e) => setLinkUrl(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") enrichFromLink(); }}
                      placeholder="https://…  (Chairish, 1stDibs, or any listing / reference page)"
                      data-testid="autofill-link-input"
                      className="w-full h-10 px-3 rounded-md border border-[#D6D3CD] text-sm focus:outline-none focus:border-[#0D1B2A]"
                    />
                    {/chairish\.com|1stdibs\.com/i.test(linkUrl) ? (
                      <label className="flex items-center gap-2 text-sm text-[#33302B] cursor-pointer">
                        <input type="checkbox" checked={linkPhotos} onChange={(e) => setLinkPhotos(e.target.checked)} data-testid="autofill-link-photos" className="h-4 w-4 accent-[#0D1B2A]" />
                        Also import my photos <span className="text-[#8A867D]">(from your own Chairish / 1stDibs listing)</span>
                      </label>
                    ) : (
                      <p className="text-xs text-[#8A867D] flex items-start gap-1.5" data-testid="autofill-link-photos-note">
                        <Camera size={13} weight="fill" className="mt-0.5 shrink-0" />
                        Details &amp; provenance only — you'll add your own photos, price &amp; shipping below.
                      </p>
                    )}
                    <button
                      onClick={enrichFromLink}
                      disabled={linkBusy || !linkUrl.trim()}
                      data-testid="autofill-link-submit"
                      className="w-full h-10 bg-[#0D1B2A] text-[#FDFBF7] rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-black transition-transform active:scale-[0.99] disabled:opacity-50"
                    >
                      <DownloadSimple size={14} weight="bold" /> {linkBusy ? "Reading the listing…" : "Pull the details"}
                    </button>
                  </div>
                )}

                <Field label="Headline" testid="field-headline">
                  <input className={inputCls} value={form.listing_headline} onChange={(e) => onField("listing_headline", e.target.value)} data-testid="input-headline" />
                </Field>

                {/* Attribution ladder — the value driver */}
                <div className="border border-[#E5DFD2] rounded-lg p-3.5 space-y-3" data-testid="attribution-section">
                  <p className="label-caps">Attribution</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Designer / Brand"><input className={inputCls} value={form.brand_designer} onChange={(e) => onField("brand_designer", e.target.value)} data-testid="input-brand" /><SuggestChips field="brand_designer" suggestions={suggestions} current={form.brand_designer} onPick={(v) => pickSuggestion("brand_designer", v)} /></Field>
                    <Field label="Maker / Manufacturer"><input className={inputCls} value={form.maker} onChange={(e) => onField("maker", e.target.value)} data-testid="input-maker" /></Field>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Artist"><input className={inputCls} value={form.artist} onChange={(e) => onField("artist", e.target.value)} data-testid="input-artist" /></Field>
                    <Field label="Styled after (in the style of)"><input className={inputCls} value={form.styled_after} onChange={(e) => onField("styled_after", e.target.value)} data-testid="input-styled-after" /></Field>
                  </div>
                  <Field label="Attribution basis">
                    <select className={inputCls} value={form.attribution_basis} onChange={(e) => onField("attribution_basis", e.target.value)} data-testid="select-attribution-basis">
                      <option value="">Select…</option>
                      {ATTR_BASIS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <p className="text-xs text-[#8A867D] mt-1">"Attributed to" / "in the style of" is not the same as "signed / labeled." Buyers &amp; platforms hold you to this — accuracy protects your listing.</p>
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Period / Era"><input className={inputCls} value={form.period_era} onChange={(e) => onField("period_era", e.target.value)} data-testid="input-era" /><SuggestChips field="period_era" suggestions={suggestions} current={form.period_era} onPick={(v) => pickSuggestion("period_era", v)} /></Field>
                  <Field label="Material"><input className={inputCls} value={form.material} onChange={(e) => onField("material", e.target.value)} data-testid="input-material" /><SuggestChips field="material" tokens suggestions={suggestions} current={form.material} onPick={(v) => pickSuggestion("material", v, true)} /></Field>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Styles"><input className={inputCls} value={form.styles} onChange={(e) => onField("styles", e.target.value)} data-testid="input-styles" placeholder="French, Neoclassical" /><SuggestChips field="styles" tokens suggestions={suggestions} current={form.styles} onPick={(v) => pickSuggestion("styles", v, true)} /></Field>
                  <Field label="Country of Origin"><input className={inputCls} value={form.country_of_origin} onChange={(e) => onField("country_of_origin", e.target.value)} data-testid="input-country" /><SuggestChips field="country_of_origin" suggestions={suggestions} current={form.country_of_origin} onPick={(v) => pickSuggestion("country_of_origin", v)} /></Field>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Dominant color"><input className={inputCls} value={form.dominant_color} onChange={(e) => onField("dominant_color", e.target.value)} data-testid="input-dominant-color" /></Field>
                  <Field label="Accent colors"><input className={inputCls} value={form.accent_colors} onChange={(e) => onField("accent_colors", e.target.value)} data-testid="input-accent-colors" placeholder="Gold, Brass" /></Field>
                </div>

                {/* Structured condition — the trust driver */}
                <div className="border border-[#E5DFD2] rounded-lg p-3.5 space-y-3" data-testid="condition-section">
                  <p className="label-caps">Condition</p>
                  <div className="grid grid-cols-3 gap-3">
                    <Field label="Type">
                      <select className={inputCls} value={form.item_condition_type} onChange={(e) => onField("item_condition_type", e.target.value)} data-testid="select-condition-type">
                        <option value="">Select…</option>
                        {COND_TYPE.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                    <Field label="Originality">
                      <select className={inputCls} value={form.original_condition} onChange={(e) => onField("original_condition", e.target.value)} data-testid="select-original-condition">
                        <option value="">Select…</option>
                        {ORIG_COND.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                    <Field label="Overall">
                      <select className={inputCls} value={form.condition_level} onChange={(e) => onField("condition_level", e.target.value)} data-testid="select-condition-level">
                        <option value="">Select…</option>
                        {COND_LEVEL.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                  </div>
                  <Field label="Condition details (be specific &amp; honest)"><input className={inputCls} value={form.condition} onChange={(e) => onField("condition", e.target.value)} data-testid="input-condition" placeholder="e.g. minor veneer chip on left front corner; recently rewired" /><SuggestChips field="condition" suggestions={suggestions} current={form.condition} onPick={(v) => pickSuggestion("condition", v)} /></Field>
                </div>

                {/* Sets & units */}
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Unit type">
                    <select className={inputCls} value={form.unit_type} onChange={(e) => onField("unit_type", e.target.value)} data-testid="select-unit-type">
                      <option value="">Select…</option>
                      {UNIT_TYPE.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label="Pieces included"><input className={inputCls} value={form.pieces_included} onChange={(e) => onField("pieces_included", e.target.value)} data-testid="input-pieces" placeholder="Set of 6: 2 arm, 4 side" /></Field>
                  <Field label="Inventory count"><input type="number" className={inputCls} value={form.inventory_count} onChange={(e) => onField("inventory_count", e.target.value)} data-testid="input-inventory-count" placeholder="1" /></Field>
                </div>

                <div>
                  <p className="label-caps mb-2">Dimensions (inches)</p>
                  <div className="grid grid-cols-3 gap-3">
                    <LabeledNum label="W" v={form.width_inches} onChange={(v) => onField("width_inches", v)} testid="input-width" />
                    <LabeledNum label="D" v={form.depth_inches} onChange={(v) => onField("depth_inches", v)} testid="input-depth" />
                    <LabeledNum label="H" v={form.height_inches} onChange={(v) => onField("height_inches", v)} testid="input-height" />
                  </div>
                </div>

                <details className="border border-[#E5DFD2] rounded-lg" data-testid="seating-dims">
                  <summary className="cursor-pointer select-none px-3.5 py-2.5 text-sm font-semibold text-[#0D1B2A]">
                    Seating dimensions <span className="font-normal text-[#8A867D]">— for chairs, sofas &amp; stools</span>
                  </summary>
                  <div className="px-3.5 pb-3.5 space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <LabeledNum label="Seat H" v={form.seat_height_inches} onChange={(v) => onField("seat_height_inches", v)} testid="input-seat-height" />
                      <LabeledNum label="Arm H" v={form.arm_height_inches} onChange={(v) => onField("arm_height_inches", v)} testid="input-arm-height" />
                      <LabeledNum label="# Seats" v={form.num_seats} onChange={(v) => onField("num_seats", v)} testid="input-num-seats" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <LabeledNum label="Seat interior W" v={form.seat_interior_w_inches} onChange={(v) => onField("seat_interior_w_inches", v)} testid="input-seat-int-w" />
                      <LabeledNum label="Seat interior D" v={form.seat_interior_d_inches} onChange={(v) => onField("seat_interior_d_inches", v)} testid="input-seat-int-d" />
                    </div>
                  </div>
                </details>

                {/* Flaw log — honest condition, each tied to a photo (anti-rejection) */}
                <div className="border border-[#E5DFD2] rounded-lg p-3.5 space-y-2.5" data-testid="flaw-log-section">
                  <div className="flex items-center justify-between">
                    <p className="label-caps">Flaw log</p>
                    <button type="button" onClick={addFlaw} data-testid="add-flaw-btn" className="inline-flex items-center gap-1 text-sm font-semibold text-[#B47A3C] hover:text-[#0D1B2A] transition-colors">
                      <Plus size={15} weight="bold" /> Add flaw
                    </button>
                  </div>
                  {(form.flaws || []).length === 0 && (
                    <p className="text-xs text-[#8A867D]">No flaws logged. Buyers reject on arrival when they're surprised — note wear, chips or repairs and tie each to the photo that shows it.</p>
                  )}
                  {(form.flaws || []).map((fl, i) => (
                    <div key={i} className="flex items-start gap-2" data-testid={`flaw-row-${i}`}>
                      <input className={inputCls + " flex-1"} value={fl.note || ""} onChange={(e) => updateFlaw(i, "note", e.target.value)} placeholder="e.g. 2in scratch on top-right corner" data-testid={`flaw-note-${i}`} />
                      <select className={inputCls + " w-28"} value={fl.image_id || ""} onChange={(e) => updateFlaw(i, "image_id", e.target.value)} data-testid={`flaw-photo-${i}`}>
                        <option value="">Photo…</option>
                        {images.map((im, idx) => <option key={im.id} value={im.id}>Photo {idx + 1}</option>)}
                      </select>
                      <button type="button" onClick={() => removeFlaw(i)} data-testid={`flaw-remove-${i}`} className="p-2 text-[#B45309] hover:bg-[#FEF2F2] rounded">
                        <Trash size={16} />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <LabeledNum label="Weight lb" v={form.weight_lbs} onChange={(v) => onField("weight_lbs", v)} testid="input-weight" />
                  <LabeledNum label="Retail $" v={form.price_retail} onChange={(v) => onField("price_retail", v)} testid="input-price" />
                  <LabeledNum label="Min $" v={form.price_minimum_acceptable} onChange={(v) => onField("price_minimum_acceptable", v)} testid="input-min-price" />
                </div>

                <PricingPanel variant="inline" itemType={selected?.item_type} brandDesigner={form.brand_designer} periodEra={form.period_era} itemCategory={selected?.item_category} condition={form.condition} onUse={(v) => onField("price_retail", v)} />

                <Field label="AI Guide Notes (shorthand for the AI)">
                  <textarea rows={2} className={inputCls + " resize-none"} value={form.ai_guide_notes} onChange={(e) => onField("ai_guide_notes", e.target.value)} placeholder="e.g. teak, recently reupholstered, minor wear on left arm" data-testid="input-guide-notes" />
                </Field>

                {/* Waterfall description */}
                {selected.is_ai_generated ? (
                  <div className="border-2 border-[#FBBF24] bg-[#FFFBEB] p-4 rounded-md relative" data-testid="ai-warning-box">
                    <span className="absolute -top-3 left-3 inline-flex items-center gap-1 bg-[#FBBF24] text-[#0D1B2A] text-xs font-bold px-2 py-0.5 rounded" data-testid="ai-generated-badge">
                      <Warning size={12} weight="fill" /> AI GENERATED
                    </span>
                    <div className="flex items-center justify-between mb-2 mt-1">
                      <p className="label-caps">Description</p>
                      <button onClick={generateAI} disabled={generating} className="text-xs font-semibold text-[#D97706] inline-flex items-center gap-1 hover:underline disabled:opacity-50" data-testid="regenerate-button">
                        <Sparkle size={13} weight="fill" /> {generating ? "Generating…" : (form.editable_final_description ? "Regenerate" : "Generate")}
                      </button>
                    </div>
                    <textarea rows={8} className="w-full bg-white border border-[#FDE68A] rounded p-2 text-base focus:outline-none focus:ring-2 focus:ring-[#FBBF24] resize-none" value={form.editable_final_description} onChange={(e) => onField("editable_final_description", e.target.value)} placeholder="Click Generate to draft a description from the details above." data-testid="input-description" />
                  </div>
                ) : (
                  <Field label="Description (dealer copy)">
                    <textarea rows={8} className={inputCls + " resize-none"} value={form.editable_final_description} onChange={(e) => onField("editable_final_description", e.target.value)} data-testid="input-description" />
                  </Field>
                )}
              </div>

              {/* Footer action bar */}
              <div className="absolute bottom-0 left-0 w-full border-t border-[#E5E5E5] bg-white p-4 pr-4 lg:pr-44 flex flex-wrap gap-y-2 gap-x-3 justify-between items-center z-40" data-testid="footer-action-bar">
                <div className="flex items-center gap-3">
                  <button onClick={() => saveDraft()} disabled={saving} className="border border-[#E5E5E5] bg-white text-[#0D1B2A] px-5 py-2 rounded-md hover:bg-gray-50 transition-colors font-medium inline-flex items-center gap-2 disabled:opacity-50" data-testid="save-draft-button">
                    <FloppyDisk size={16} /> {saving ? "Saving…" : "Save Draft"}
                  </button>
                  {user?.is_admin && selected.status !== "Exported" && (
                    <button onClick={demoFill} disabled={demoFilling} data-testid="demo-fill-button" title="Admin/demo only — fills remaining fields & photos with clearly-labeled sample data" className="border border-dashed border-[#B47A3C] text-[#B47A3C] px-4 py-2 rounded-md hover:bg-[#FBF3E9] transition-colors font-medium inline-flex items-center gap-2 whitespace-nowrap shrink-0 disabled:opacity-50">
                      <MagicWand size={16} weight="fill" /> {demoFilling ? "Filling…" : "Finish to 100% (demo)"}
                    </button>
                  )}
                  {usage && selected.status !== "Exported" && (
                    <span data-testid="free-trial-indicator" className={`text-sm font-semibold ${usage.locked ? "text-[#92400E]" : "text-[#525252]"}`}>
                      {usage.unlimited
                        ? "Unlimited cross-listing"
                        : usage.plan === "free"
                        ? (usage.locked ? "Free listings used up" : `${usage.remaining} of ${usage.free_limit} free listings left`)
                        : (usage.locked ? `${usage.monthly_cap} monthly cross-lists used` : `${usage.remaining} of ${usage.monthly_cap} cross-lists left this month`)}
                    </span>
                  )}
                </div>
                <button onClick={handleCrossList} className="bg-[#0D1B2A] text-white px-5 py-2 rounded-md hover:bg-black transition-colors font-medium inline-flex items-center gap-2 shadow-sm whitespace-nowrap shrink-0" data-testid="approve-crosslist-button">
                  {selected.status === "Exported" ? <><CheckCircle size={16} weight="fill" /> Cross-Listed</> : usage?.locked ? <><Crown size={16} weight="fill" /> Upgrade to Cross-List</> : <><ListChecks size={16} weight="fill" /> Review &amp; Cross-List</>}
                </button>
              </div>
            </>
          )}
        </section>
      </div>

      <UpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)} usage={usage} />
      <PhotoChecklistDialog open={checklistOpen} onClose={() => setChecklistOpen(false)} count={images.length} max={SLOTS} />
      <ReadinessDialog
        open={readinessOpen}
        onClose={() => setReadinessOpen(false)}
        data={readiness}
        platformKey={readinessPlatform}
        setPlatformKey={setReadinessPlatform}
        onProceed={proceedCrossList}
        status={selected?.status}
        loading={readinessLoading}
      />
      <UnderTheHoodSheet open={engineOpen} onClose={() => setEngineOpen(false)} listing={selected} />
    </div>
  );
}

function UpgradeModal({ open, onClose, usage }) {
  const navigate = useNavigate();
  const monthlyCap = usage?.plan && usage.plan !== "free";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="upgrade-modal">
        <DialogHeader>
          <div className="inline-flex items-center gap-2 text-[#D97706] mb-1">
            <Crown size={20} weight="fill" />
            <span className="label-caps">{monthlyCap ? "Monthly cap reached" : "Free listings complete"}</span>
          </div>
          <DialogTitle className="font-serif-display text-3xl font-bold tracking-tight">
            {monthlyCap
              ? `You've used your ${usage?.monthly_cap} cross-lists this month`
              : `You've finished your ${usage?.free_limit || 20} free listings`}
          </DialogTitle>
          <DialogDescription className="text-[#525252]">
            {monthlyCap
              ? "Go annual for unlimited cross-listing — perfect if you've got a big catalog to move."
              : "Members unlock the money layer: best-fit placement, price ranges, list-ready copy, and cross-list export. One good flip covers months of membership."}
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-[#E5E5E5] bg-[#FDFBF7] p-4 mt-1">
          <div className="flex items-baseline gap-2">
            <span className="font-serif-display text-3xl font-bold">$39</span>
            <span className="text-sm text-[#737373]">/mo · or $360/yr (unlimited)</span>
          </div>
          <p className="text-sm text-[#525252] mt-1">Founding Council members lock in $29/mo for life. Everything you've reviewed stays saved.</p>
        </div>
        <button
          data-testid="see-plans-button"
          onClick={() => { onClose(); navigate("/plans"); }}
          className="mt-2 w-full py-3 rounded-md font-semibold text-sm inline-flex items-center justify-center gap-2 bg-[#0D1B2A] text-white hover:bg-black transition-colors"
        >
          <Lightning size={15} weight="fill" /> See membership plans
        </button>
        <p className="text-xs text-center text-[#A3A3A3] mt-1">Prepaid · cancel anytime · no surprise renewals</p>
      </DialogContent>
    </Dialog>
  );
}

const inputCls = "flex h-11 w-full rounded-md border border-[#D4D4D4] bg-white px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-[#0D1B2A]";

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <p className="label-caps mb-1.5">{label}</p>
      {children}
    </div>
  );
}

function LabeledNum({ label, v, onChange, testid }) {
  return (
    <div>
      <div className="relative">
        <input type="number" step="any" value={v} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="h-11 w-full rounded-md border border-[#D4D4D4] bg-white pl-3 pr-2 text-base focus:outline-none focus:ring-2 focus:ring-[#0D1B2A]" />
      </div>
      <p className="text-xs text-[#737373] mt-1 text-center font-medium">{label}</p>
    </div>
  );
}


function SuggestChips({ field, suggestions, current, onPick, tokens }) {
  const list = suggestions?.[field] || [];
  const cur = (current || "").trim().toLowerCase();
  const curTokens = tokens ? cur.split(/[,/]/).map((s) => s.trim()).filter(Boolean) : [];
  const avail = list
    .filter((v) => (tokens ? !curTokens.includes(v.toLowerCase()) : v.toLowerCase() !== cur))
    .slice(0, 6);
  if (!avail.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid={`suggest-${field}`}>
      <span className="text-xs font-semibold uppercase tracking-wide text-[#92400E] inline-flex items-center gap-1">
        <Sparkle size={12} weight="fill" /> From your past listings
      </span>
      {avail.map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => onPick(v)}
          data-testid={`suggest-${field}-chip`}
          title={tokens ? `Add "${v}"` : `Use "${v}"`}
          className="text-sm px-2.5 py-1 rounded-full border border-[#E5DFD2] bg-[#FBF7EF] text-[#0D1B2A] hover:border-[#0D1B2A] hover:bg-white transition-colors"
        >
          {v}
        </button>
      ))}
    </div>
  );
}


function ChecklistBlock({ title, items, icon: Icon, color }) {
  return (
    <div>
      <p className="text-sm font-bold uppercase tracking-wider text-[#0D1B2A] mb-2">{title}</p>
      <ul className="space-y-1.5">
        {items.map((s) => (
          <li key={s} className="flex items-start gap-2 text-[15px] text-[#404040] leading-snug">
            <Icon size={16} weight="fill" className="mt-0.5 shrink-0" style={{ color }} /> {s}
          </li>
        ))}
      </ul>
    </div>
  );
}

function PhotoChecklistDialog({ open, onClose, count, max }) {
  const enough = count >= 3;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="photo-checklist-modal">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-3xl font-bold tracking-tight flex items-center gap-2">
            <Camera size={26} weight="fill" className="text-[#B47A3C]" /> Photo checklist
          </DialogTitle>
          <DialogDescription className="text-[#525252]">
            Start with clear, well-lit photos. Great photos sell faster — and keep your listing from being rejected.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div
            data-testid="photo-checklist-status"
            className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold ${enough ? "bg-[#ECFDF5] text-[#047857]" : "bg-[#FFFBEB] text-[#B45309]"}`}
          >
            {enough ? <CheckCircle size={18} weight="fill" /> : <Warning size={18} weight="fill" />}
            {count}/{max} photos added{enough ? " — you're good to go." : ` — add at least ${3 - count} more (minimum 3).`}
          </div>

          <div className="border-l-4 border-[#B47A3C] bg-[#FBF7F0] p-4 rounded-r-md">
            <p className="font-semibold text-[#0D1B2A] mb-1.5 flex items-center gap-2">
              <Images size={16} weight="fill" className="text-[#B47A3C]" /> Cover photo &amp; background
            </p>
            <p className="text-[15px] text-[#525252] leading-relaxed">
              Your cover photo should show the <strong>entire piece in the frame</strong>. If your listing includes
              more than one piece (a pair or a set), make sure <strong>every piece is completely visible</strong> in
              the cover shot. Some platforms will erase the background for you — but it's best practice to shoot the
              rest against a <strong>solid, contrasting background</strong> to keep your listings consistent and professional.
            </p>
          </div>

          <ChecklistBlock title="Shots to capture" items={PHOTO_SHOTS} icon={Camera} color="#B47A3C" />
          <ChecklistBlock title="Image requirements" items={PHOTO_REQS} icon={CheckCircle} color="#047857" />
          <ChecklistBlock title="Best practices" items={PHOTO_TIPS} icon={Sparkle} color="#B47A3C" />

          <button
            onClick={onClose}
            data-testid="photo-checklist-done"
            className="w-full h-12 bg-[#0D1B2A] text-white rounded-md font-medium hover:bg-black transition-colors"
          >
            Got it
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReadinessDialog({ open, onClose, data, platformKey, setPlatformKey, onProceed, status, loading }) {
  const platforms = data?.platforms || [];
  const plat = platforms.find((p) => p.key === platformKey) || platforms[0];
  const exported = status === "Exported";
  const missing = plat ? plat.required_total - plat.required_met : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="readiness-modal">
        <DialogHeader>
          <div className="inline-flex items-center gap-2 text-[#B47A3C] mb-1">
            <ListChecks size={20} weight="fill" />
            <span className="label-caps">Cross-list readiness</span>
          </div>
          <DialogTitle className="font-serif-display text-3xl font-bold tracking-tight">
            Is this piece ready to cross-list?
          </DialogTitle>
          <DialogDescription className="text-[#525252]">
            We check your listing against each marketplace's must-haves — so it's not turned away after all your work.
            You're always in control: fix what's missing, or cross-list anyway.
          </DialogDescription>
        </DialogHeader>

        {loading || !plat ? (
          <div className="py-12 text-center text-[#737373]" data-testid="readiness-loading">Checking your listing…</div>
        ) : (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-2" data-testid="readiness-platform-toggle">
              {platforms.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPlatformKey(p.key)}
                  data-testid={`readiness-platform-${p.key}`}
                  className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors inline-flex items-center gap-1.5 ${p.key === plat.key ? "bg-[#0D1B2A] text-white border-[#0D1B2A]" : "bg-white text-[#0D1B2A] border-[#E5DFD2] hover:border-[#0D1B2A]"}`}
                >
                  {p.ready
                    ? <CheckCircle size={14} weight="fill" className={p.key === plat.key ? "text-[#6EE7B7]" : "text-[#047857]"} />
                    : <Warning size={14} weight="fill" className={p.key === plat.key ? "text-[#FCD34D]" : "text-[#B45309]"} />}
                  {p.name}
                </button>
              ))}
              <span className="text-xs text-[#A3A3A3] px-3 py-1.5 rounded-full border border-dashed border-[#D4D4D4]" data-testid="readiness-more-coming">
                More marketplaces coming
              </span>
            </div>

            <div
              className={`rounded-lg p-4 flex items-center justify-between ${plat.ready ? "bg-[#ECFDF5] border border-[#A7F3D0]" : "bg-[#FFFBEB] border border-[#FDE68A]"}`}
              data-testid="readiness-score"
            >
              <div className="flex items-center gap-3">
                {plat.ready
                  ? <SealCheck size={30} weight="fill" className="text-[#047857]" />
                  : <Warning size={30} weight="fill" className="text-[#B45309]" />}
                <div>
                  <p className={`font-bold text-lg leading-tight ${plat.ready ? "text-[#047857]" : "text-[#B45309]"}`}>
                    {plat.ready
                      ? `Ready for ${plat.name}`
                      : `${missing} thing${missing === 1 ? "" : "s"} left for ${plat.name}`}
                  </p>
                  <p className="text-sm text-[#525252]">
                    {plat.required_met} of {plat.required_total} required · {plat.recommended_met} of {plat.recommended_total} recommended
                  </p>
                </div>
              </div>
              <div className="text-right">
                <span className={`font-serif-display text-3xl font-bold ${plat.ready ? "text-[#047857]" : "text-[#B45309]"}`}>{plat.score}%</span>
                <p className="text-xs text-[#737373]">complete</p>
              </div>
            </div>

            <div>
              <p className="text-sm font-bold uppercase tracking-wider text-[#0D1B2A] mb-2">Required by {plat.name}</p>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5" data-testid="readiness-required-list">
                {plat.required.map((it) => (
                  <li
                    key={it.key}
                    data-testid={`readiness-required-${it.key}`}
                    className={`flex items-start gap-2 text-[15px] leading-snug ${it.present ? "text-[#404040]" : "text-[#B45309] font-semibold"}`}
                  >
                    {it.present
                      ? <CheckCircle size={17} weight="fill" className="mt-0.5 shrink-0 text-[#047857]" />
                      : <Warning size={17} weight="fill" className="mt-0.5 shrink-0 text-[#D97706]" />}
                    {it.label}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="text-sm font-bold uppercase tracking-wider text-[#0D1B2A] mb-2">
                Recommended <span className="text-[#A3A3A3] font-medium normal-case tracking-normal">— strengthens your listing</span>
              </p>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5" data-testid="readiness-recommended-list">
                {plat.recommended.map((it) => (
                  <li
                    key={it.key}
                    data-testid={`readiness-recommended-${it.key}`}
                    className={`flex items-start gap-2 text-[15px] leading-snug ${it.present ? "text-[#404040]" : "text-[#737373]"}`}
                  >
                    {it.present
                      ? <CheckCircle size={17} weight="fill" className="mt-0.5 shrink-0 text-[#047857]" />
                      : <Circle size={17} className="mt-0.5 shrink-0 text-[#D4D4D4]" />}
                    {it.label}
                  </li>
                ))}
              </ul>
            </div>

            {!exported && (
              <div className="flex flex-col sm:flex-row gap-3 pt-1">
                <button
                  onClick={onClose}
                  data-testid="readiness-go-fix"
                  className="flex-1 h-12 rounded-md border border-[#E5E5E5] bg-white text-[#0D1B2A] font-medium hover:bg-gray-50 transition-colors inline-flex items-center justify-center gap-2"
                >
                  <ArrowLeft size={16} /> Go fix what's missing
                </button>
                <button
                  onClick={onProceed}
                  data-testid="readiness-proceed"
                  className="flex-1 h-12 rounded-md bg-[#0D1B2A] text-white font-medium hover:bg-black transition-colors inline-flex items-center justify-center gap-2"
                >
                  <CheckCircle size={16} weight="fill" /> {plat.ready ? "Cross-list now" : "Cross-list anyway"}
                </button>
              </div>
            )}
            {!plat.ready && !exported && (
              <p className="text-xs text-center text-[#A3A3A3]" data-testid="readiness-agency-note">
                Your call — you can always cross-list now and refine later. We just make sure you know what each marketplace expects.
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
